/** Individual toast notification with auto-dismiss and close button.
 *
 *  Rebuilt in Phase 2 of the redesign to use the design tokens. See
 *  docs/plans/2026-04-28-frontend-redesign.md §3 (visual language).
 */

import { useEffect, useState } from 'react'
import type { ToastVariant } from '../../hooks/useToast'

// Each variant uses dark ink on a saturated tint, matching the badge pattern
// from §4.2 of the redesign plan. Hairline border in the same hue.
const variantClasses: Record<ToastVariant, string> = {
  success: 'border-capital text-capital',
  error: 'border-risk text-risk',
  warning: 'border-amber text-amber',
  info: 'border-rule-2 text-bone',
}

const variantIcons: Record<ToastVariant, string> = {
  success: '✓',
  error: '✗',
  warning: '⚠',
  info: 'ℹ',
}

interface ToastProps {
  id: string
  variant: ToastVariant
  message: string
  duration: number
  onDismiss: (id: string) => void
}

export function Toast({ id, variant, message, duration, onDismiss }: ToastProps) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(false)
      setTimeout(() => onDismiss(id), 180)
    }, duration)
    return () => clearTimeout(timer)
  }, [id, duration, onDismiss])

  const handleClose = () => {
    setVisible(false)
    setTimeout(() => onDismiss(id), 180)
  }

  return (
    <div
      role="alert"
      data-testid={`toast-${id}`}
      className={`flex items-start gap-3 border bg-ink px-4 py-3 transition-all duration-200 ${variantClasses[variant]} ${
        visible ? 'translate-x-0 opacity-100' : 'translate-x-4 opacity-0'
      }`}
    >
      <span className="mt-0.5 font-mono text-sm font-bold" aria-hidden="true">
        {variantIcons[variant]}
      </span>
      <p className="flex-1 font-mono text-[13px] text-bone">{message}</p>
      <button
        onClick={handleClose}
        className="font-mono text-xs text-muted hover:text-bone"
        aria-label="Close notification"
      >
        &#x2715;
      </button>
    </div>
  )
}
